import { Fragment, extractSentences, isBlockElement } from "./Benespeak.js";
import AnnotationChime from "./annotator/AnnotationChime";
import { TtsState } from "./TtsState";

/**
 * @import {TtsSentence} from "./Benespeak.js"
 */

const WORD_CHAR_MATCHER = /\w/;
class TtsPlayer {
  static get SEEK_FORWARD() {
    return 0;
  }

  static get SEEK_BACKWARD() {
    return 1;
  }

  constructor(options = {}) {
    /** @type {SpeechSynthesisVoice} */
    this.voice = options.voice || null;
    this.rate = options.rate || 1;
    this.voiceImageDescriptions = options.voiceImageDescriptions || true;
    this.voicePageNumbers = options.voicePageNumbers || false;
    this.treeWalker = null;
    this.annotationsEnabled = options.annotationsEnabled;

    /** @type {Element} Top-most element that the player will attempt to play. Defaults to document.body. */
    this.root = options.root || document.body;

    const noOp = () => {};

    /** @type {Function} */
    this.highlightWord = options.highlightWord || noOp;

    /** @type {Function} */
    this.clearWordHighlight = options.clearWordHighlight || noOp;

    /** @type {Function} */
    this.highlightSentence = options.highlightSentence || noOp;

    /** @type {Function} */
    this.clearSentenceHighlight = options.clearSentenceHighlight || noOp;

    /** @type {Function} */
    this.updateViewerPosition = options.updateViewerPosition || noOp;

    /** @type {Function} */
    this.onSpeechEnd = options.onSpeechEnd || noOp;

    /** @type {Function} */
    this.onOutOfElements =
      options.onOutOfElements || options.onSpeechEnd || noOp;

    /** @type {Function} Called when a rewind runs off the top of the readable content. */
    this.onOutOfPreviousElements =
      options.onOutOfPreviousElements || options.onSpeechEnd || noOp;

    /** @type {Function} */
    this.onBeforeReadingSentence =
      options.onBeforeReadingSentence || (() => null);

    /** @type {() => TtsState} Reads the current playback state at call time. Injected by the provider that owns the state. */
    this.getState = options.getState || (() => TtsState.Playing);

    /** @type {AnnotationChime} Produces the chime sounds when the TTS encounters a note. */
    this.annotationChime = options.annotationChime || new AnnotationChime();

    /** @type {Set<string>} Locations of notes already read this session, guarding against re-reading a note that spans multiple sentences. */
    this.spokenNoteLocations = new Set();

    /** @type {Element} The element used to generate current sentence data. */
    this.activeElement = null;

    /** @type {Range} The "virtual cursor position" for the TTS player. */
    this.cursorPosition = null;

    /** @type {Array<TtsSentence>} Sentence data. */
    this.sentences = [];

    /** @type {Number} Array index of the most recently uttered sentence. */
    this.currentSentence = -1;

    /** @type {SpeechSynthesisUtterance} Most recent dispatched Utterance*/
    this.utterance = null;

    // shorter ref to speech synthesis global
    this.synth = options.synth || window.speechSynthesis;

    // Internal state tracking
    this.interrupted = false;
    this.stopped = false;

    /**
     * @type {boolean} Indicates that when resumed, the TTS will need to start a new utterance
     * True in two conditions
     *  - Google voice are being used - they cannot be held onto for more than 10 seconds
     *  - User skips back or forwards in TTS while paused - start from the beginning
     *    of the new sentence when TTS is played again
     */
    this.pausedWithCancelledUtterance = false;
  }

  /**
   * @param {Element} rootElement Root of the DOM tree that TTS Player should read.
   */
  set root(rootElement) {
    this.reset();

    if (rootElement === null || rootElement === undefined) {
      this.treeWalker = null;
    } else if (rootElement?.nodeType !== Node.ELEMENT_NODE) {
      throw new TypeError("TTSPlayer root must be a DOM element");
    }

    this.treeWalker =
      rootElement?.ownerDocument?.createTreeWalker(
        rootElement,
        NodeFilter.SHOW_TEXT + NodeFilter.SHOW_ELEMENT,
        (n) => this._nodeFilter(n),
      ) ?? null;
  }

  /**
   * @returns {Element | null} Root of the DOM subtree that the TTS Player is reading.
   */
  get root() {
    return this.treeWalker?.root ?? null;
  }

  get isUsingGoogleVoice() {
    return this.voice?.name?.startsWith("Google") ?? false;
  }

  /**
   * Starts TTS playback from the sentence closest to the click coordinates in a
   * double-clicked target.
   * @param {MouseEvent} event
   */
  playFromMouseEvent(event) {
    // Interrupt any ongoing speech
    this.interrupt();

    // Bail if it's an anchor tag. Double-clicks typically won't trigger on anchor
    // elements, but best to be safe and ensure we don't run TTS.
    if (event.target.tagName === "A") {
      this.onSpeechEnd();
      return;
    }

    // Grab the selection
    const selection = event.target.ownerDocument.defaultView.getSelection();

    // Find the closest
    const targetElement = this._findClosestReadableAncestor(
      selection.anchorNode,
    );

    // Mouse events should not trigger playback on the root element.
    if (targetElement === null || targetElement === this.treeWalker?.root) {
      this.reset();
    } else {
      this._generateSentenceData(targetElement);
    }

    // By default, read from the beginning of the sentence set
    let idx = 0;

    // If we have sentence data, and the selection contains a valid range,
    // check if the range is inside any of the sentence ranges.
    if (this.sentences?.length > 0 && selection.rangeCount === 1) {
      const rangeFromSelection = selection.getRangeAt(0);
      idx = this._findIndexForRange(rangeFromSelection);
    }

    // Delegate to play method; if no sentences were found for targetElement,
    // the play method will attempt to advance to the next readable element,
    // and exits cleanly when no more elements can be consumed.
    this.play(targetElement, idx);
  }

  /**
   * Starts TTS playback from the closest readable Element ancestor of the given DOM node.
   * @param {Node} node
   */
  playFromNode(node) {
    // Interrupt any ongoing speech
    this.interrupt();

    // find closest element parent
    this.play(this._findClosestReadableAncestor(node));
  }

  /**
   * Starts playback from the last sentence of the last readable element under
   * the current root. Used when rewinding across a section boundary so the
   * reader lands on the final sentence of the newly displayed (previous)
   * section, then continues forward from there.
   */
  playFromEnd() {
    this.interrupt();
    const element = this._seekToLastReadableElement();
    if (element === null) {
      // Nothing readable here (e.g. an empty section); keep crossing backward.
      this.reset();
      this.onOutOfPreviousElements();
    } else {
      this.play(element, -1);
    }
  }

  /**
   * Generates sentence data for the element and resolves the index of the
   * sentence to act on, seeking forward through elements with no readable
   * content as needed. Leaves this.activeElement and this.sentences set to the
   * resolved element.
   * @param {Element} element
   * @param {Number} [index] Sentence index; when omitted, derived from the cursor.
   * @returns {Number | null} Index into this.sentences, or null if no readable
   *                          content remains.
   */
  _resolveSentenceIndex(element, index) {
    this._generateSentenceData(element);

    if (this.sentences.length > 0) {
      return index !== undefined
        ? index
        : this._findIndexForRange(this.cursorPosition);
    }

    const nextReadableElement = this._seekToNextReadableElement(element);
    return nextReadableElement !== null
      ? this._resolveSentenceIndex(nextReadableElement, undefined)
      : null;
  }

  /**
   * Starts TTS playback from the specified element.
   * @param {Element} element Element to read.
   * @param {Number} [index] Index of sentence to start reading from. If none is provided,
   *                         the reader will attempt to locate the "virtual cursor" in the
   *                         sentence data and read from there.
   */
  play(element, index) {
    const sentenceIndex = this._resolveSentenceIndex(element, index);
    if (sentenceIndex !== null) {
      this._speakUtterance(sentenceIndex);
    } else {
      this.reset();
      this.onOutOfElements();
    }
  }

  /**
   * Reset TTS sentence and position data.
   */
  reset() {
    this.activeElement = null;
    this.sentences = null;
    this.currentSentence = -1;
    this.cursorPosition = null;
  }

  /**
   * Generates sentence data from the specified element.
   * @param {Element} element
   */
  _generateSentenceData(element) {
    if (element !== this.activeElement) {
      console.log(
        `Generating new data for ${element.tagName}#${element.getAttribute("id")}`,
      );
      this.activeElement = element;
      this.sentences = extractSentences(element, {
        voiceImageDescriptions: this.voiceImageDescriptions,
        voicePageNumbers: this.voicePageNumbers,
      });
    }
  }

  /**
   * Speaks the sentence at startIndex. Negative start indexes go from the end of the
   * sentence list (e.g. _speakUtterance(-1) speaks the last sentence).
   * @param {Number} startIndex
   */
  _speakUtterance(startIndex) {
    // timers to pause and resume speech every 10 seconds
    // allows for google voices to not stop in middle of speech
    var timeout = setInterval(() => this._googleVoiceKeepAlive(), 10000);
    const idx =
      startIndex >= 0 ? startIndex : this.sentences.length + startIndex;
    const s = this.sentences[idx];

    this._handleAnnotationAudio(s.range);

    const u = new SpeechSynthesisUtterance(s.utterance);
    u.voice = this.voice;
    u.rate = this.rate;
    u.btIdx = idx;
    const handler = this.makeTtsEventHandler(s, idx, this, timeout);
    u.addEventListener("start", handler);
    u.addEventListener("boundary", handler);
    u.addEventListener("error", handler);
    u.addEventListener("end", handler);
    this.synth.speak(u);
  }

  /**
   * Marks the start of a user-initiated reading session. Primes the chime's
   * audio context (must run on a user gesture so the browser allows playback)
   * and clears the per-session chime/note debounce state.
   */
  beginSession() {
    if (this.annotationsEnabled) {
      this.annotationChime.prime();
      this.annotationChime.reset();
    }
    this.spokenNoteLocations.clear();
  }

  /**
   * Asks the host for the annotations overlapping the given sentence range, then
   * strikes the chime if found. Reads every note in the sentence that hasn't been
   * read yet, in the order the host supplies them (earliest location first), so a
   * sentence covering more than one note voices them all in reading order.
   * @param {Range} range
   * @returns {void}
   */
  _handleAnnotationAudio(range) {
    const overlaps = this.onBeforeReadingSentence(range);
    if (!overlaps) return null;
    if (this.annotationsEnabled) {
      this.annotationChime.dispatch(overlaps);
    }
    const unreadNotes = (overlaps.notes ?? []).filter(
      (note) => note?.note && !this.spokenNoteLocations.has(note.location),
    );
    if (unreadNotes.length === 0) return;
    this.highlightSentence(range);
    this.updateViewerPosition(range); // optional: also scroll it into view
    for (const note of unreadNotes) {
      this.spokenNoteLocations.add(note.location);
      this._speakNote(note.note);
    }
  }

  /**
   * Speaks a one-off bit of text (an annotation note) with the current voice and
   * rate. Intentionally has no event handlers: it is purely informational and
   * must not advance the sentence cursor or fire highlight callbacks.
   * @param {string} text
   */
  _speakNote(text) {
    const u = new SpeechSynthesisUtterance(text);
    u.voice = this.voice;
    u.rate = this.rate;
    this.synth.speak(u);
  }

  /**
   * Releases audio resources owned by the player (the chime's audio context).
   * Call when the player is being torn down.
   */
  dispose() {
    if (this.annotationsEnabled) {
      this.annotationChime.dispose();
    }
  }

  resume() {
    if (this.pausedWithCancelledUtterance) {
      // The queue was cancelled (Google-voice pause or a skip while paused),
      // so there is nothing to resume — replay from the current sentence.
      this.pausedWithCancelledUtterance = false;
      this.play(this.activeElement, this.currentSentence);
    } else {
      this.synth.resume();
    }
  }

  pause() {
    if (this.isUsingGoogleVoice) {
      // Chrome's speechSynthesis.pause() does not hold Google voices
      // indefinitely — it auto-resumes after ~10s. Cancel the utterance
      // instead and remember the sentence so resume() can replay it.
      this.pausedWithCancelledUtterance = true;
      this.synth.cancel();
    } else {
      this.synth.pause();
    }
  }

  _googleVoiceKeepAlive() {
    if (!this.synth.paused && this.isUsingGoogleVoice) {
      this.synth.pause();
      this.synth.resume();
    }
  }

  /**
   * Skips ahead one sentence based on the current TTS state. While Playing the
   * new sentence is spoken. While Paused the cursor and highlight move to the
   * new sentence but no audio plays and the player stays paused — resume()
   * picks up from the new position. While Stopped this is a no-op.
   */
  ffwd() {
    this.spokenNoteLocations.clear();

    const state = this.getState();
    if (state === TtsState.Stopped) return;
    const shouldPlay = state === TtsState.Playing;
    this.interrupt();
    const newIndex = this.currentSentence + 1;

    if (newIndex < this.sentences.length) {
      console.log(`Skipping to sentence ${newIndex}`);
      this._goToSentence(this.activeElement, newIndex, shouldPlay);
    } else {
      const nextReadableElement = this._seekToNextReadableElement(
        this.activeElement,
      );
      if (nextReadableElement !== null) {
        this._goToSentence(nextReadableElement, undefined, shouldPlay);
      } else if (shouldPlay) {
        // No more readable nodes
        this.reset();
        this.onOutOfElements();
      }
    }
  }

  /**
   * Skips back one sentence. See ffwd for the per-state behavior.
   */
  rew() {
    this.spokenNoteLocations.clear();

    const state = this.getState();
    if (state === TtsState.Stopped) return;
    const shouldPlay = state === TtsState.Playing;
    this.interrupt();
    const newIndex = this.currentSentence - 1;

    if (newIndex >= 0) {
      console.log(`Rewinding to sentence ${newIndex}`);
      this._goToSentence(this.activeElement, newIndex, shouldPlay);
    } else {
      const nextReadableElement = this._seekToNextReadableElement(
        this.activeElement,
        TtsPlayer.SEEK_BACKWARD,
      );
      if (nextReadableElement !== null) {
        this._goToSentence(nextReadableElement, undefined, shouldPlay);
      } else if (shouldPlay) {
        // Ran off the top of this section; hand off so the host can cross into
        // the previous section and resume from its last sentence. Mirrors ffwd.
        this.reset();
        this.onOutOfPreviousElements();
      }
    }
  }

  /**
   * Moves to a sentence, either speaking it (shouldPlay) or silently
   * repositioning the cursor and highlight while staying paused.
   * @param {Element} element
   * @param {Number} [index] Sentence index; when omitted, derived from the cursor.
   * @param {boolean} shouldPlay
   */
  _goToSentence(element, index, shouldPlay) {
    if (shouldPlay) {
      this.play(element, index);
    } else {
      this._showSentenceWhilePaused(element, index);
    }
  }

  /**
   * Moves the cursor and sentence highlight to the target sentence without
   * speaking, leaving the player paused. Sets pausedWithCancelledUtterance so
   * resume() replays from here and the stray end event from a cancelled
   * utterance keeps the new highlight. If no readable content remains, stays
   * paused where it is rather than navigating.
   * @param {Element} element
   * @param {Number} [index] Sentence index; when omitted, derived from the cursor.
   */
  _showSentenceWhilePaused(element, index) {
    const sentenceIndex = this._resolveSentenceIndex(element, index);
    if (sentenceIndex === null) return;

    const sentence = this.sentences[sentenceIndex];
    this.currentSentence = sentenceIndex;
    this.cursorPosition = sentence.range;
    this.clearWordHighlight();
    this.clearSentenceHighlight();
    this.highlightSentence(sentence.range);
    this.updateViewerPosition(sentence.range);
    this.pausedWithCancelledUtterance = true;
  }

  /**
   * Cancels all utterances in queue and sets the stopped flag. This will reset player state
   * when the speechEnd event fires.
   */
  stop() {
    this.pausedWithCancelledUtterance = false;
    this.stopped = true;
    this.synth.cancel();
  }

  /**
   * Cancels all utterances in queue and sets the interrupted flag. The interrupt flag
   * is used for pauses and ffwd/rew to indicate to the player that it should hang on to
   * player state when the speechEnd event fires, as the next utterance will likely be from
   * the current activeElement.
   */
  interrupt() {
    if (this.pausedWithCancelledUtterance) {
      // A cancel-based pause leaves the sentence highlight visible so the user
      // can see where they paused. Interrupting (ffwd/rew/click) means we're
      // moving off that sentence, so clear the lingering highlight now — no
      // utterance is in flight to fire an end event that would do it for us.
      this.clearWordHighlight();
      this.clearSentenceHighlight();
    }
    this.pausedWithCancelledUtterance = false;
    this.interrupted = true;
    this.synth.cancel();
  }

  /**
   * Handler for end-of-utterance event. Since Web Speech doesn't make a distinction between
   * the utterance coming to a natural stop or being manually canceled/interrupted, we need
   * to perform some checks to decide the correct course of action.
   */
  handleSentenceEnd() {
    if (this.pausedWithCancelledUtterance) {
      // The utterance was cancelled while paused. Hold position; resume() will
      // replay the current sentence.
      return;
    } else if (this.stopped) {
      // Manually stopped. Data should be cleared out.
      this.reset();
      this.onSpeechEnd();
    } else if (this.synth.paused) {
      // User paused during the inter-utterance gap. Hold position; the next
      // play action (resume / skip) will pick up from currentSentence.
      return;
    } else if (!this.interrupted) {
      // Not stopped or interrupted, which means that the utterance came to a natural stop.
      if (this.currentSentence + 1 < this.sentences.length) {
        // If there are more sentences remaining, play the next one
        this.play(this.activeElement, this.currentSentence + 1);
      } else {
        // If we're out of sentences, see if there are any more elements to play.
        const nextReadableElement = this._seekToNextReadableElement(
          this.activeElement,
        );
        if (nextReadableElement !== null) {
          this.play(nextReadableElement);
        } else {
          // No more readable nodes
          this.reset();
          this.onOutOfElements();
        }
      }
    }
  }

  /**
   * @param {TtsSentence} s Sentence data
   * @param {Number} i Index of sentence within sentence array
   * @param {TtsPlayer} player TtsPlayer object
   * @returns {Function} Handler function for SpeechSynthesisUtterance events
   */
  makeTtsEventHandler(s, i, player, timeout) {
    return function (event) {
      switch (event.type) {
        case "start":
          player.highlightSentence(s.range);
          player.updateViewerPosition(s.range);
          player.currentSentence = i;
          player.cursorPosition = s.range;
          player.interrupted = false;
          player.stopped = false;
          player.pausedWithCancelledUtterance = false;
          break;
        case "boundary":
          // Don't do any of this if speech is stopped or interrupted
          if (!player.stopped && !player.interrupted) {
            if (event.name === "word") {
              const wordIndexes = s.wordsIn(event.charIndex, event.charLength);
              player.clearWordHighlight();
              if (wordIndexes.length > 0) {
                const range = Fragment.spannedRange(
                  s.words[wordIndexes[0]],
                  s.words[wordIndexes[wordIndexes.length - 1]],
                );
                player.highlightWord(range);
                player.updateViewerPosition(range);
              }
            }
          }
          break;
        case "error":
        case "end":
          if (!player.pausedWithCancelledUtterance) {
            // When we cancel the utterance to pause a Google voice, keep the
            // sentence highlight visible so the user can see where they paused.
            player.clearWordHighlight();
            player.clearSentenceHighlight();
          }
          player.handleSentenceEnd();
          clearInterval(timeout);
          break;
        default:
        // shrug
      }
    };
  }

  /**
   * Locates the closest Element ancestor of the provided Node that contains readable content.
   * @param {Node} fromNode
   * @returns {Element} Closest readable element ancestor
   */
  _findClosestReadableAncestor(fromNode) {
    if (
      fromNode.nodeType === Node.ELEMENT_NODE &&
      (this._isAtRoot(fromNode) || this._shouldRead(fromNode))
    ) {
      return fromNode;
    }
    const parentElement = fromNode?.parentElement;
    return parentElement !== null
      ? this._findClosestReadableAncestor(parentElement)
      : null;
  }

  /**
   * Bidirectional search for readable elements in a document tree.
   * @param {Element} fromElement
   * @param {Number} direction
   * @returns {Element} Next Element containing readable content, or null if no following readable element exists.
   */
  _seekToNextReadableElement(fromElement, direction = TtsPlayer.SEEK_FORWARD) {
    this.treeWalker.currentNode = fromElement;
    let node = null;
    if (direction === TtsPlayer.SEEK_BACKWARD) {
      node = this.treeWalker.previousSibling();
    } else {
      node = this.treeWalker.nextSibling();
    }

    if (node !== null) {
      const r = node.ownerDocument.createRange();
      if (node.nodeType === Node.ELEMENT_NODE) {
        // For element nodes, the virtual cursor is positioned as a range surrounding the element.
        r.setStartBefore(node);
        r.setEndAfter(node);
      } else {
        // For text nodes, set the virtual cursor by using a collapsed range.
        // For forward seeks, use the first occurence of a word character.
        // For backwards seeks, use the *last* occurence of a word character.
        if (direction === TtsPlayer.SEEK_BACKWARD) {
          r.setStart(node, node.textContent.match(/\w(\W*)$/)?.index);
        } else {
          r.setStart(node, node.textContent.match(WORD_CHAR_MATCHER)?.index);
        }
        r.collapse();
      }

      this.cursorPosition = r;

      return this._findClosestReadableAncestor(node);
    } else {
      return null;
    }
  }

  /**
   * Finds the last readable element under the root by walking every accepted
   * node in document order and returning the readable-block ancestor of the
   * final one. Returns null if the root contains no readable content.
   * @returns {Element | null}
   */
  _seekToLastReadableElement() {
    if (this.treeWalker === null) {
      return null;
    }

    let node = this.treeWalker.root;
    while (node.lastChild !== null) {
      node = node.lastChild;
    }
    this.treeWalker.currentNode = node;

    const lastNode =
      this._nodeFilter(node) === NodeFilter.FILTER_ACCEPT
        ? node
        : this.treeWalker.previousNode();

    return lastNode !== null
      ? this._findClosestReadableAncestor(lastNode)
      : null;
  }

  /**
   * TODO: We might be able to ditch this and fold it into the node filter.
   * @param {Element} element
   * @returns {boolean} true if the element is as low as we can practically go in the DOM tree.
   *                    Generally this means the element contains non-empty text nodes, non-block
   *                    children, or is itself announceable.
   */
  _shouldRead(element) {
    if (
      this._isVisible(element) &&
      (this._isAnnounceableElement(element) ||
        (isBlockElement(element) &&
          (hasReadableTextNodeChildren(element) ||
            hasNonBlockChildren(element))))
    ) {
      return true;
    } else {
      return false;
    }
  }

  /**
   * @param {Node} node
   * @returns {boolean} True if the node is visible.
   */
  _isVisible(node) {
    let element =
      node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
    return (
      element.hidden === false &&
      element.ownerDocument.defaultView.getComputedStyle(element).display !==
        "none"
    );
  }

  /**
   * Some elements don't present textual content, but can be announced if the user
   * opts to have it read out. Main examples of this are images (which may have alt text)
   * and page numbers, which are usually marked up as empty spans or divs.
   * @param {Element} element
   * @returns {boolean} True if the element should be considered readable.
   */
  _isAnnounceableElement(element) {
    return (
      element.nodeType === Node.ELEMENT_NODE &&
      ((this.voiceImageDescriptions && element.tagName === "IMG") ||
        (this.voicePageNumbers &&
          element.getAttribute("epub:type") === "pagebreak") ||
        (this.voicePageNumbers &&
          element.getAttribute("role") === "doc-pagebreak"))
    );
  }

  /**
   * @param {Element} element
   * @returns {boolean} Returns true if the element matches the TtsPlayer's root element.
   */
  _isAtRoot(element) {
    return element === this.treeWalker?.root;
  }

  _nodeFilter(n) {
    if (!this._isVisible(n)) {
      return NodeFilter.FILTER_REJECT;
    } else if (
      isReadableTextNode(n) ||
      this._isAnnounceableElement(n) ||
      isMathJaxNode(n)
    ) {
      return NodeFilter.FILTER_ACCEPT;
    }

    return NodeFilter.FILTER_SKIP;
  }

  /**
   * Locates sentence index that overlaps with Node n.
   * @param {Node} n
   */
  _findIndexForNode(n) {
    const r = n.ownerDocument.createRange();
    if (n.nodeType === Node.ELEMENT_NODE) {
      r.setStartBefore(n);
    } else {
      r.setStart(n, n.textContent.match(WORD_CHAR_MATCHER)?.index ?? 0);
    }
    r.collapse();
    return this._findIndexForRange(r);
  }

  /**
   * Locates sentence index that overlaps with Range r. If an overlap
   * cannot be found, we fall back to index 0.
   * @param {Range} r
   * @returns {number}
   */
  _findIndexForRange(r) {
    if (
      r !== null &&
      r.commonAncestorContainer.ownerDocument === this.root.ownerDocument
    ) {
      for (let i = 0; i < this.sentences.length; i++) {
        if (this.sentences[i].containsRange(r)) {
          return i;
        }
      }
    }
    return 0;
  }
}

// The functions that follow are separate from the class as they are pure utility
// functions and do not use instance state variables to do their work.

/**
 * @param {Element} element
 * @returns {boolean} True if element has text children that contain word characters.
 */
export function hasReadableTextNodeChildren(element) {
  if (element.nodeType === Node.ELEMENT_NODE) {
    for (const n of element.childNodes) {
      if (isReadableTextNode(n)) {
        return true;
      }
    }
  }
  return false;
}

/**
 * @param {Node} node
 * @returns {boolean} True if text node contains word characters.
 */
export function isReadableTextNode(node) {
  return (
    node.nodeType === Node.TEXT_NODE &&
    node.textContent.match(WORD_CHAR_MATCHER)
  );
}

export function isMathJaxNode(node) {
  return node.tagName === "MJX-CONTAINER";
}

/**
 * @param {Element} element
 * @returns {boolean} True if element has non-block element children
 */
export function hasNonBlockChildren(element) {
  if (element.nodeType === Node.ELEMENT_NODE) {
    for (const e of element.children) {
      if (!isBlockElement(e)) {
        return true;
      }
    }
  }
  return false;
}

export default TtsPlayer;
